import React, {Component} from 'react';
import {ENCODED_QUERY_STRING, FORBIDDEN, NOT_FOUND} from "../../constants/routes";
import {
    getDecodedUnitIDRecordingID,
    getFormattedRouteForRecordingViewForSharing, getFormattedRouteForRecordingViewForView
} from "../unit/recordings/utils";
import {dbase} from "../../firebase/index";
import {byPropKey} from "../utils";
import Throbber from "../miscellaneous/Throbber";
import {parse} from "query-string";
import {
    IS_PERSIST_EDIT_MODE_OFF, IS_PERSIST_EDIT_MODE_ON, PERSIST_KEY
} from "../unit/recordings/recordingcard/RecordingOptionsDropdownMenu";
import RecordingForm from "./RecordingForm";
import {
    showEditRecordingSavedFailureNotification, showEditRecordingSavedSuccessNotification,
    showReadRecordingNotification
} from "../notifications/live";
import {getRecordingType, onShareRecording, PRIVACY_PUBLIC} from "./utils";
import {connect} from "react-redux";
import {getUnit} from "../unit/utils";

export const STYLE_RECORDING_INFO_ITEM = {
    float: 'left',
    marginRight: '10px'
};

/** Timeout in milliseconds for persisted auth state to carry over from firebase. */
const TIMEOUT = 30000;

/** Retry delay in milliseconds for persisted auth to attempt reloading from firebase. */
const TIMEOUT_RETRY = TIMEOUT / 12;

/**
 * Recording page component.
 */
class Recording extends Component {
    constructor(props) {
        super(props);
        const recordingHash = getDecodedUnitIDRecordingID(props.match.params[ENCODED_QUERY_STRING]);
        this.state = {
            ...recordingHash,
            recording: null,
            unit: null,
            authorized: false,
            showEditButton: false,
            editModeOn: false,
            newRecording: {},
            error: null
        };
    }

    onEditButtonClick = () => {
        this.setState(byPropKey('editModeOn', true));
    };

    offEditMode = () => {
        this.setState(byPropKey('editModeOn', false));
        this.setState(byPropKey('newRecording', {}));
    };

    /**
     * Pass the extension field data back to parent via
     * {@param newRecording} object extension of the
     */
    onFieldChange = (newRecording) => {
        this.setState(byPropKey('newRecording', {
            ...this.state.newRecording, ...newRecording
        }));
    };

    saveChanges = (event) => {
        if (!this.isInvalid()) {
            dbase.setUpdatedRecordingData(this.state.unitID, this.state.recordingID, this.state.newRecording)
                .then(() => {
                    showEditRecordingSavedSuccessNotification();
                    this.loadRecording();
                })
                .then(() => {
                    this.offEditMode();
                })
                .catch(showEditRecordingSavedFailureNotification);
        } else {
            showEditRecordingSavedFailureNotification();
        }
        event.preventDefault();
    };

    isInvalid = () => {
        const isNewTitleTooLong = this.state.newRecording && this.state.newRecording.title && this.state.newRecording.title.length > 120;
        const isNewDescriptionTooLong = this.state.newRecording && this.state.newRecording.description && this.state.newRecording.description.length > 500;
        const isNewPriorityInvalid = this.state.newRecording && !this.state.newRecording.priority;
        const isNewPrivacyInvalid = this.state.newRecording && !this.state.newRecording.privacy;
        return isNewTitleTooLong || isNewDescriptionTooLong || isNewPriorityInvalid || isNewPrivacyInvalid;
    };

    componentDidMount() {
        if (!this.state.unitID || !this.state.recordingID) {
            this.props.history.push(NOT_FOUND);
        } else {
            this.loadRecording();
        }
    }

    /**
     * Loads the recording onto the page or redirect user to 403.
     */
    loadRecording = () => {
        if (PERSIST_KEY in parse(this.props.location.search)) {
            // Case if we detect url was routed from somewhere where authUser was (possibly) previously present
            // WARNING: we do not want to actually load data if it is private until the authUser is verified just in case.
            if (!this.props.authUser){
                // Case for unverified authUser in the Redux store - we recursively queue authorization handling logic.
                // 1. Queue a timeout for a recursive reload attempt.
                this.retryTimeout = setTimeout(() => {
                    if (!this.state.recording) {
                        this.loadRecording();
                        clearTimeout(this.retryTimeout);
                        this.retryTimeout = null;
                    }
                }, TIMEOUT_RETRY);

                // 2. Queue a timeout for a forbidden attempt.
                this.authorizationTimeout = setTimeout(() => {
                    if (!this.props.authUser) {
                        this.props.history.push(FORBIDDEN);
                        clearTimeout(this.authorizationTimeout);
                        this.authorizationTimeout = null;
                    }
                }, TIMEOUT);
            } else {
                // Case for a verified authUser in the Redux store - we can begin to load the recording.
                // 1. Get the units belonging to the user first.
                dbase.getUnitsByUserOnce(this.props.authUser.uid)
                    .then(snapshot => {
                        if (snapshot.val()[this.state.unitID]) {
                            // Case for if the user is authorized to access the current unit
                            // 2. Set the authorized state and show the edit button.
                            if (parse(this.props.location.search)[PERSIST_KEY] === IS_PERSIST_EDIT_MODE_ON) {
                                // Switch on edit mode if the page was loaded without the persistence set to TRUE
                                this.setState(byPropKey('editModeOn', true));
                                // Push page to the false mode so we won't go to edit mode on refresh again.
                                this.props.history.push(getFormattedRouteForRecordingViewForView(
                                    this.props.location.pathname,
                                    {[PERSIST_KEY]:IS_PERSIST_EDIT_MODE_OFF}
                                ));
                            }
                            this.setState(byPropKey('showEditButton', true));
                            this.setState(byPropKey('authorized', true));
                            // 3. Load the recording for the unit
                            dbase.doOnRecordingValueChanged(this.state.unitID, this.state.recordingID,
                                snapshot => {
                                    if (snapshot.val()) {
                                        // Case for recording found
                                        this.setState(byPropKey('recording', snapshot.val()));
                                        this.updateRecordingRead();
                                    } else {
                                        // Case for invalid hash/ 404
                                        this.props.history.push(NOT_FOUND);
                                    }
                                });
                            this.loadUnit();
                        } else {
                            // Case for if the user is NOT authorized to access the current unit
                            // Push the user to the forbidden page.
                            this.props.history.push(FORBIDDEN);
                        }
                    })
                    .catch(error => {
                        this.setState(byPropKey('error', error));
                    });
            }
        } else {
            // Case for a shared link

            // Get the recording
            dbase.doOnRecordingValueChanged(this.state.unitID, this.state.recordingID,
                snapshot => {
                    const recordingData = snapshot.val();
                    if (recordingData.privacy !== PRIVACY_PUBLIC) {
                        // If the recording is not public, just throw the user to forbidden immediately.
                        this.props.history.push(FORBIDDEN);
                    } else {
                        // Otherwise, display the recording, regardless of authUser state.
                        this.setState(byPropKey('recording', recordingData));
                        this.setState(byPropKey('authorized', true));
                        this.loadUnit();
                    }
                });
        }
    };

    componentWillUnmount() {
        if (this.state.recordingID && this.state.unitID) {
            dbase.doOffRecordingValueChanged(this.state.unitID, this.state.recordingID);
        }
    }

    /**
     * Updates the recording to status: read on successful load
     */
    updateRecordingRead = () => {
        if (this.state.recording.isUnread) {
            dbase.setRecordingIsRead(this.state.unitID, this.state.recordingID)
                .then(showReadRecordingNotification);
        }
    };

    /**
     * Loads the unit onto the local state to be displayed.
     */
    loadUnit = () => {
        dbase.getUnitByUnitIdOnce(this.state.unitID)
            .then(snapshot => {
                this.setState(byPropKey('unit', getUnit(this.state.unitID, snapshot.val())));
            })
            .catch(error => {
                this.setState(byPropKey('error', error));
            })
    };

    /**
     * Handles the share button clicked event.
     */
    onShareClick = () => {
        const recordingUrl = getFormattedRouteForRecordingViewForSharing(this.state.unitID, this.state.recordingID);
        onShareRecording(this.state.recording.privacy, recordingUrl, this.state.unitID, this.state.recordingID)();
    };

    render() {
        const onShareClick = this.onShareClick;
        return (
            <div className="container">
                <div className="row" style={{margin: '15px auto'}}>
                    <div style={{float: 'left'}} className="col-4"/>
                    <div className="col-8 text-right">
                        {
                            this.state.recording &&
                            !this.state.editModeOn &&
                            <button
                                type="button"
                                className="btn btn-outline-dark mr-2"
                                onClick={onShareClick}
                            >
                                <i className="fas fa-share"/> Share
                            </button>
                        }
                        {
                            this.state.showEditButton &&
                            !this.state.editModeOn &&
                            <button className="btn btn-outline-dark" onClick={this.onEditButtonClick}><i className="fas fa-edit"/> Edit</button>
                        }
                        {
                            this.state.editModeOn &&
                            <div>
                                <button className="btn btn-success mr-2" onClick={this.saveChanges}><i className="far fa-save"/> Save</button>
                                <button className="btn btn-warning" onClick={this.offEditMode}><i className="fas fa-times"/> Cancel</button>
                            </div>
                        }
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-2 col-md-1"/>
                    {
                        this.state.recording &&
                        this.state.unit &&
                        this.state.authorized
                            ? (
                                <div className="col-lg-8 col-md-10 col-sm-12 col-xs-12">
                                    <dl8-video
                                        poster={this.state.recording.thumbnailURL}
                                        format="MONO_360"
                                        seekable
                                        style={{maxHeight: 'calc(100vh - 250px)', minHeight: 150}}
                                    >
                                        {
                                            // Note: Only some video elements have a URL so
                                            // we add them only when present to prevent failures.
                                        }
                                        {
                                            this.state.recording.wowzaURL &&
                                            <source
                                                src={this.state.recording.wowzaURL}
                                                type={getRecordingType(this.state.recording.wowzaURL)}
                                            />
                                        }
                                        {
                                            this.state.recording.wowzaURL2 &&
                                            <source
                                                src={this.state.recording.wowzaURL2}
                                                type={getRecordingType(this.state.recording.wowzaURL2)}
                                            />
                                        }
                                    </dl8-video>
                                    {
                                        this.state.recording &&
                                        this.state.unit &&
                                        <RecordingForm
                                            recording={this.state.recording}
                                            unit={this.state.unit}
                                            editModeOn={this.state.editModeOn}
                                            onFieldChange={this.onFieldChange}
                                            saveChanges={this.saveChanges}
                                            offEditMode={this.offEditMode}
                                        />
                                    }
                                </div>
                            )
                            : <div className="col-lg-8 col-md-10 col-sm-12 col-xs-12 text-center"><Throbber/></div>
                    }
                    <div className="col-lg-2 col-md-1"/>
                </div>
            </div>
        );
    }
}

/**
 * Map Redux {@param state} to component props
 */
const mapStateToProps = (state) => ({
    authUser: state.sessionState.authUser
});

export default connect(mapStateToProps)(Recording);



// WEBPACK FOOTER //
// ./src/components/recording/Recording.jsx